const { useState, useEffect, useRef, useCallback } = React;
const LucideIcons = typeof window !== 'undefined' && window.LucideReact ? window.LucideReact : {};
const {
  Package = () => null, Download = () => null,
  Settings = () => null, LogOut = () => null, Upload = () => null,
  Inbox = () => null, ChevronLeft = () => null,
  Check = () => null, RefreshCw = () => null, Search = () => null,
  LayoutDashboard = () => null, ArrowRight = () => null, Home = () => null,
  GripVertical = () => null, ChevronUp = () => null, ChevronDown = () => null, BarChart2 = () => null,
  DollarSign = () => null, CreditCard = () => null, TrendingUp = () => null,
  AlertTriangle = () => null, Plus = () => null, Trash2 = () => null, Edit3 = () => null,
  BookOpen = () => null, Truck = () => null, FileSpreadsheet = () => null,
  SlidersHorizontal = () => null, Eye = () => null, EyeOff = () => null, Save = () => null,
  Map: MapIcon = () => null,
  CheckCircle2 = () => null, XCircle = () => null, X = () => null, Copy = () => null,
  KeyRound = () => null, Lock = () => null, Zap = () => null,
  LifeBuoy = () => null, MessageSquare = () => null, Paperclip = () => null,
  Image: ImageIcon = () => null, Clock3 = () => null, UserCircle = () => null,
  ShieldCheck = () => null, CircleCheck = () => null, Users = () => null,
  Send = () => null, Mail = () => null, ExternalLink = () => null, Sparkles = () => null,
  EllipsisVertical = () => null, FileText = () => null,
} = LucideIcons;

// ─── TWEAKS PANEL ──────────────────────────────────────────────

const useTweaks = (defaults) => {
  const [values, setValues] = useState(() => {
    try {
      const saved = localStorage.getItem('p2s_tweaks');
      return saved ? { ...defaults, ...JSON.parse(saved) } : { ...defaults };
    } catch { return { ...defaults }; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onMsg = e => {
      if (e.data?.type === '__activate_edit_mode')   setOpen(true);
      if (e.data?.type === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const setTweak = useCallback((key, val) => {
    setValues(prev => {
      const next = { ...prev, [key]: val };
      try { localStorage.setItem('p2s_tweaks', JSON.stringify(next)); } catch {}
      return next;
    });
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [key]: val } }, '*');
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const TweaksPanel = ({ children }) => {
    if (!open) return null;
    return (
      <div style={{ position:'fixed', bottom:20, right:20, zIndex:9999, background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.14)', width:260, fontFamily:'Inter,sans-serif', overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid #F3F4F6', background:'#F9FAFB' }}>
          <span style={{ fontSize:12, fontWeight:700, color:'#374151', letterSpacing:'0.04em' }}>TWEAKS</span>
          <button onClick={dismiss} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#9CA3AF', lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:'12px 0', maxHeight:420, overflowY:'auto' }}>
          {React.Children.map(children, child => React.cloneElement(child, { values, setTweak }))}
        </div>
      </div>
    );
  };

  return { tweaks: values, setTweak, TweaksPanel };
};

const TweakSection = ({ title, children, values, setTweak }) => (
  <div style={{ marginBottom:4 }}>
    <div style={{ fontSize:10, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.1em', padding:'4px 16px 6px' }}>{title}</div>
    {React.Children.map(children, child => child ? React.cloneElement(child, { values, setTweak }) : null)}
  </div>
);

const TweakToggle = ({ id, label, values, setTweak }) => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 16px' }}>
    <span style={{ fontSize:13, color:'#374151' }}>{label}</span>
    <div onClick={() => setTweak(id, !values[id])} style={{ width:36, height:20, borderRadius:3, background: values[id] ? '#73D04B' : '#D1D5DB', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
      <div style={{ position:'absolute', top:3, left: values[id] ? 18 : 3, width:14, height:14, borderRadius:2, background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}></div>
    </div>
  </div>
);

const TweakColor = ({ id, label, values, setTweak }) => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 16px' }}>
    <span style={{ fontSize:13, color:'#374151' }}>{label}</span>
    <input type="color" value={values[id]||'#000'} onChange={e => setTweak(id, e.target.value)} style={{ width:32, height:24, border:'1px solid #E5E7EB', borderRadius:4, cursor:'pointer', padding:1 }}/>
  </div>
);

// ─── TWEAK DEFAULTS ────────────────────────────────────────────
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accentBlue": "#006FDD",
  "accentGreen": "#73D04B",
  "sidebarBg": "#1E2433",
  "tableCompact": false,
  "showRawData": true,
  "dateFormat": "DD-MM-YYYY"
}/*EDITMODE-END*/;

// ─── REMEMBERED ACCOUNTS ───────────────────────────────────────
const ACCOUNT_SWITCHER_STORAGE_KEY = 'p2s_remembered_accounts';
const ACCOUNT_SWITCHER_PENDING_KEY = 'p2s_pending_account_switch';

const normalizeRememberedAccount = account => ({
  email: String(account?.email || '').trim(),
  isAdmin: !!account?.isAdmin,
  lastUsedAt: account?.lastUsedAt || new Date().toISOString(),
});

const readRememberedAccounts = () => {
  try {
    const accounts = JSON.parse(localStorage.getItem(ACCOUNT_SWITCHER_STORAGE_KEY) || '[]');
    return Array.isArray(accounts) ? accounts.map(normalizeRememberedAccount).filter(account => account.email) : [];
  } catch { return []; }
};

const writeRememberedAccounts = accounts => {
  try { localStorage.setItem(ACCOUNT_SWITCHER_STORAGE_KEY, JSON.stringify(accounts)); } catch {}
};

const rememberAccount = user => {
  if (!user) return readRememberedAccounts();
  const current = normalizeRememberedAccount({
    email: user.email,
    isAdmin: user.isAdmin,
    lastUsedAt: new Date().toISOString(),
  });
  const key = current.email;
  if (!key) return readRememberedAccounts();
  const next = [
    current,
    ...readRememberedAccounts().filter(account => account.email !== key),
  ].slice(0, 6);
  writeRememberedAccounts(next);
  return next;
};

const setPendingAccountSwitch = account => {
  try { localStorage.setItem(ACCOUNT_SWITCHER_PENDING_KEY, JSON.stringify(normalizeRememberedAccount(account))); } catch {}
};

const clearPendingAccountSwitch = () => {
  try { localStorage.removeItem(ACCOUNT_SWITCHER_PENDING_KEY); } catch {}
};

const consumePendingAccountSwitch = () => {
  try {
    const raw = localStorage.getItem(ACCOUNT_SWITCHER_PENDING_KEY);
    localStorage.removeItem(ACCOUNT_SWITCHER_PENDING_KEY);
    return raw ? normalizeRememberedAccount(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const SmartCsvIcon = ({ size = 24, color = '#111827', strokeWidth = 2.1 }) => (
  <span style={{ position:'relative', width:size, height:size, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
    <FileSpreadsheet size={size} color={color} strokeWidth={strokeWidth}/>
    <Sparkles size={Math.max(10, Math.round(size * 0.42))} color="#006FDD" strokeWidth={2.4} style={{ position:'absolute', right:-3, top:-4, background:'#fff', borderRadius:4 }}/>
  </span>
);

// ─── PLATFORM CONFIG ───────────────────────────────────────────
const PLATFORM_CFG = {
  stripe:      { label: 'Stripe',      color: '#635BFF', bg: '#F0EFFE', char: 'S', logo: '/assets/payment_processors/logo/stripe.webp' },
  paypal:      { label: 'PayPal',      color: '#0070BA', bg: '#EEF6FF', char: 'P', logo: '/assets/payment_processors/logo/paypal.webp' },
  shopify:     { label: 'Shopify',     color: '#008060', bg: '#E8F5EF', char: 'S', logo: '/assets/payment_processors/logo/shopify.webp' },
  woocommerce: { label: 'WooCommerce', color: '#7F54B3', bg: '#F3EEF9', char: 'W', logo: '/assets/payment_processors/logo/woocommerce.webp' },
  kickstarter: { label: 'Kickstarter', color: '#05CE78', bg: '#E6FAF2', char: 'K',  logo: '/assets/payment_processors/logo/kickstarter.webp' },
  indiegogo:   { label: 'Indiegogo',   color: '#EB1478', bg: '#FCE3F1', char: 'IG', logo: '/assets/payment_processors/logo/indiegogo.webp' },
  gamefound:   { label: 'Gamefound',   color: '#2BA84A', bg: '#E3F5EA', char: 'GF', logo: '/assets/payment_processors/logo/gamefound.webp' },
  kickstarter_csv: { label: 'Kickstarter', color: '#05CE78', bg: '#E6FAF2', char: 'K',  logo: '/assets/payment_processors/logo/kickstarter.webp' },
  indiegogo_csv:   { label: 'Indiegogo',   color: '#EB1478', bg: '#FCE3F1', char: 'IG', logo: '/assets/payment_processors/logo/indiegogo.webp' },
  gamefound_csv:   { label: 'Gamefound',   color: '#2BA84A', bg: '#E3F5EA', char: 'GF', logo: '/assets/payment_processors/logo/gamefound.webp' },
  manual_csv:  { label: 'Smart CSV',    color: '#111827', bg: '#F3F4F6', char: 'CSV', Icon: SmartCsvIcon },
  amazon:      { label: 'Amazon',      color: '#FF9900', bg: '#FFF8EE', char: 'A',  logo: '/assets/payment_processors/logo/amazon.webp' },
  etsy:        { label: 'Etsy',        color: '#F1641E', bg: '#FFF4ED', char: 'E',  logo: '/assets/payment_processors/logo/etsy.webp' },
  square:      { label: 'Square',      color: '#111827', bg: '#F3F4F6', char: 'S',  logo: '/assets/payment_processors/logo/square.webp' },
  bigcommerce: { label: 'BigCommerce', color: '#121118', bg: '#F3F4F6', char: 'BC', logo: '/assets/payment_processors/logo/bigcommerce_logo.webp' },
  adobe_commerce: { label: 'Adobe Commerce', color: '#EB1000', bg: '#FEF2F2', char: 'AC', logo: '/assets/payment_processors/logo/adobe_commerce.webp' },
  manual:      { label: 'Manual',      color: '#111827', bg: '#F3F4F6', char: 'M', Icon: Edit3 },
};

const AVAILABLE_ORDER_SOURCES = [
  { id:'stripe',      name:'Stripe',         type:'stripe', category:'Payments', method:'OAuth', description:'Syncs checkout orders, customers, line items, payment status, and refund details where available.' },
  { id:'paypal',      name:'PayPal',          type:'paypal', category:'Payments', method:'OAuth', description:'Syncs paid transactions, payer details, item names, totals, and payment status.' },
  { id:'square',      name:'Square',          type:'assisted', category:'Payments', method:'Assisted', description:'Imports payment orders, customer details, line items, and totals with support-assisted setup.' },
  { id:'shopify',     name:'Shopify',         type:'shopify', category:'E-commerce', method:'OAuth', description:'Syncs store orders, customers, shipping addresses, line items, SKUs, and order status.' },
  { id:'woocommerce', name:'WooCommerce',     type:'woocommerce', category:'E-commerce', method:'App auth', description:'Syncs store orders, billing and shipping details, products, SKUs, totals, and status.' },
  { id:'bigcommerce', name:'BigCommerce',     type:'assisted', category:'E-commerce', method:'Assisted', description:'Imports store orders, customers, addresses, products, SKUs, and order status with support-assisted setup.' },
  { id:'adobe_commerce', name:'Adobe Commerce', type:'adobe_commerce', category:'E-commerce', method:'Integration', description:'Syncs Adobe Commerce orders, customers, addresses, line items, SKUs, and status.' },
  { id:'kickstarter', name:'Kickstarter CSV', type:'csv', category:'Crowdfunding', method:'CSV', description:'Imports backer pledges, reward tiers, survey responses, shipping details, and payment status.' },
  { id:'indiegogo',   name:'Indiegogo CSV',   type:'csv', category:'Crowdfunding', method:'CSV', description:'Imports contributions, perks, contributor details, shipping data, and payment status.' },
  { id:'gamefound',   name:'Gamefound CSV',   type:'csv', category:'Crowdfunding', method:'CSV', description:'Imports pledges, pledge tiers, backer details, shipping data, and payment status.' },
  { id:'amazon',      name:'Amazon',          type:'assisted', category:'Marketplaces', method:'Assisted', description:'Imports marketplace orders, buyer details, shipping addresses, item names, and totals with support-assisted setup.' },
  { id:'etsy',        name:'Etsy',            type:'assisted', category:'Marketplaces', method:'Assisted', description:'Imports shop orders, buyers, shipping addresses, item names, quantities, and totals with support-assisted setup.' },
  { id:'manual_csv', name:'Smart CSV Import', type:'csv', category:'Universal imports', method:'CSV', description:'Imports order exports from any source and maps columns into warehouse-ready records.' },
].map(source => ({ ...source, ...(PLATFORM_CFG[source.id] || {}) }));

const SOURCE_TAB_GROUPS = [
  'Payments',
  'E-commerce',
  'Crowdfunding',
  'Marketplaces',
  'Universal imports',
].map(category => ({
  title:category,
  desc:'Order sources Omnifill can normalize into fulfillment-ready records.',
  items:AVAILABLE_ORDER_SOURCES.filter(src => src.category === category),
}));

const SOURCE_TAB_SOURCE_IDS = AVAILABLE_ORDER_SOURCES.map(source => source.id);

const normalizeProductName = value => {
  const name = String(value ?? '').trim();
  if (!name) return '';
  const canonical = name.replace(/\s+/g, ' ').toLowerCase();
  return ['n/a', 'na', 'not applicable', 'none', 'null', 'undefined'].includes(canonical) ? '' : name;
};
const orderItemsForDisplay = order => {
  const items = Array.isArray(order?.items) && order.items.length
    ? order.items
    : [{
        raw_name: order?.product,
        product_id: order?.product_id,
        product_name: order?.product_name,
        product_sku: order?.product_sku,
        product_image_data: order?.product_image_data,
        quantity: order?.qty || 1,
      }];
  return items.map((item, index) => ({
    ...item,
    position: item.position ?? index,
    quantity: Number(item.quantity || item.qty || 1) || 1,
    display_name: item.product_name || normalizeProductName(item.raw_name || item.product || item.name) || 'N/A',
    display_sku: item.product_sku || item.sku || '',
  }));
};
const orderNeedsProductDefinition = order => orderItemsForDisplay(order).some(item => normalizeProductName(item.raw_name || item.product || item.display_name) && !item.product_id);

const COMMERCIAL_INVOICE_REASON_OPTIONS = [
  'Commercial sale',
  'Replacement',
  'Commercial sample',
  'Return',
  'Gift',
];
const COMMERCIAL_INVOICE_INCOTERM_OPTIONS = ['DAP', 'DDP', 'FCA', 'EXW'];

const customsInvoiceItemsForOrder = order => {
  const items = orderItemsForDisplay(order);
  const orderTotal = Number(order.amount);
  return items.map((item, index) => {
    const quantity = Number(item.quantity || 1) || 1;
    const lineAmount = Number(item.amount);
    const fallbackLineAmount = items.length === 1 && Number.isFinite(orderTotal) && orderTotal > 0 ? orderTotal : null;
    const effectiveLineAmount = Number.isFinite(lineAmount) && lineAmount > 0 ? lineAmount : fallbackLineAmount;
    return {
      key:item.id || `${item.position ?? index}-${item.display_name}`,
      name:item.display_name,
      sku:item.display_sku || '',
      description:item.product_customs_description || '',
      hsCode:item.product_hs_code || '',
      origin:item.product_country_of_origin || '',
      quantity,
      unitValue:effectiveLineAmount === null ? '' : (effectiveLineAmount / quantity).toFixed(2),
      netWeightKg:item.product_net_weight_kg ?? '',
      grossWeightKg:item.product_gross_weight_kg ?? '',
    };
  });
};

const invoiceImageForPdf = source => new Promise(resolve => {
  if (!source || /^data:image\/(?:png|jpe?g);base64,/i.test(source)) {
    resolve(source || '');
    return;
  }
  const image = new Image();
  image.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      canvas.getContext('2d').drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch {
      resolve('');
    }
  };
  image.onerror = () => resolve('');
  image.src = source;
});

const commercialInvoiceIncoterm = (incoterm, city, country) => {
  const code = String(incoterm || '').trim().toUpperCase();
  if (code !== 'DDP') return code;
  const namedPlace = [cleanCity(city), country].filter(Boolean).join(', ');
  return namedPlace ? `DDP ${namedPlace} (Incoterms 2020)` : 'DDP (Incoterms 2020)';
};

const commercialInvoiceDraftForOrder = (order, profile = {}) => ({
  invoiceNumber:`${profile.invoicePrefix || 'INV'}-${order.id}`,
  invoiceDate:/^\d{4}-\d{2}-\d{2}$/.test(String(order.date || '').slice(0, 10))
    ? String(order.date).slice(0, 10)
    : new Date().toISOString().slice(0, 10),
  shipment:{
    waybill:'',
    packageCount:1,
    reasonExport:profile.defaultReasonExport || COMMERCIAL_INVOICE_REASON_OPTIONS[0],
    incoterm:profile.defaultIncoterm || '',
    freightStatement:profile.defaultFreightStatement || '',
    items:customsInvoiceItemsForOrder(order),
  },
});

const validateCommercialInvoiceDraft = ({ profile, order, invoiceNumber, invoiceDate, shipment }) => {
  if (!profile?.businessName?.trim() || !profile?.address?.trim()) {
    return { error:'Complete the seller name and address in Invoice settings first.' };
  }
  if (!String(invoiceNumber || '').trim() || !/^\d{4}-\d{2}-\d{2}$/.test(String(invoiceDate || ''))) {
    return { error:'Add a valid invoice number and date.' };
  }
  if (!profile.eori?.trim()) {
    return { error:'Add the exporter EORI in Invoice settings first.' };
  }
  if (!shipment.reasonExport || !shipment.incoterm || !(Number(shipment.packageCount) > 0)) {
    return { error:'Add the export reason, Incoterm, and package count.' };
  }
  if (shipment.incoterm === 'DDP' && (!cleanCity(order.city) || !String(order.country || '').trim())) {
    return { error:'DDP requires the destination city and country on the order.' };
  }
  const incompleteItem = shipment.items.find(item =>
    !String(item.description || '').trim()
    || String(item.hsCode || '').trim().length < 6
    || !/^[A-Z]{2}$/.test(String(item.origin || '').trim().toUpperCase())
    || !(Number(item.unitValue) > 0)
    || !(Number(item.netWeightKg) > 0)
    || !(Number(item.grossWeightKg) > 0)
  );
  if (incompleteItem) {
    return { error:`Complete the customs description, HS code, origin, unit value, net weight, and gross weight in Products for ${incompleteItem.name}.` };
  }
  const totalNetWeight = shipment.items.reduce((sum, item) => sum + (Number(item.netWeightKg) * Number(item.quantity || 1)), 0);
  const grossWeightKg = shipment.items.reduce((sum, item) => sum + (Number(item.grossWeightKg) * Number(item.quantity || 1)), 0);
  if (grossWeightKg < totalNetWeight) {
    return { error:`Gross weight must be at least the ${totalNetWeight.toFixed(3)} kg total net weight.` };
  }
  return {
    error:'',
    totalNetWeight,
    grossWeightKg,
  };
};

const CommercialInvoiceShipmentFields = ({ order, shipment, onShipmentChange }) => {
  const fieldStyle = { width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:'#fff', color:'#1A1A2E' };
  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12 }}>
        <label style={{ fontSize:11, fontWeight:800, color:'#6B7280' }}>Tracking / waybill (optional)<input value={shipment.waybill} onChange={event => onShipmentChange('waybill', event.target.value)} placeholder="Carrier tracking or air waybill number" style={{ ...fieldStyle, marginTop:5 }}/></label>
        <label style={{ fontSize:11, fontWeight:800, color:'#6B7280' }}>Packages per order<input type="number" min="1" step="1" value={shipment.packageCount} onChange={event => onShipmentChange('packageCount', event.target.value)} style={{ ...fieldStyle, marginTop:5 }}/></label>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12 }}>
        <label style={{ fontSize:11, fontWeight:800, color:'#6B7280' }}>Reason for export
          <select value={shipment.reasonExport} onChange={event => onShipmentChange('reasonExport', event.target.value)} style={{ ...fieldStyle, marginTop:5, cursor:'pointer' }}>
            {COMMERCIAL_INVOICE_REASON_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <label style={{ fontSize:11, fontWeight:800, color:'#6B7280' }}>Incoterm
          <select value={shipment.incoterm} onChange={event => onShipmentChange('incoterm', event.target.value)} style={{ ...fieldStyle, marginTop:5, cursor:'pointer' }}>
            <option value="">Choose</option>
            {COMMERCIAL_INVOICE_INCOTERM_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
          {shipment.incoterm === 'DDP' && order && (
            <span style={{ display:'block', marginTop:5, color:'#6B7280', fontSize:11, fontWeight:500 }}>
              Invoice: {commercialInvoiceIncoterm('DDP', order.city, order.country)}
            </span>
          )}
        </label>
      </div>
      <label style={{ fontSize:11, fontWeight:800, color:'#6B7280' }}>Freight statement (optional)
        <input value={shipment.freightStatement} onChange={event => onShipmentChange('freightStatement', event.target.value)} placeholder="e.g. Freight included in DDP price" maxLength={300} style={{ ...fieldStyle, marginTop:5 }}/>
      </label>
    </>
  );
};

const CommercialInvoiceFields = ({
  order,
  invoiceNumber,
  invoiceDate,
  shipment,
  onInvoiceNumberChange,
  onInvoiceDateChange,
  onShipmentChange,
  onShipmentItemChange,
}) => {
  const fieldStyle = { width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:'#fff', color:'#1A1A2E' };
  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12 }}>
        <label style={{ fontSize:11, fontWeight:800, color:'#6B7280' }}>Invoice number<input value={invoiceNumber} onChange={event => onInvoiceNumberChange(event.target.value)} style={{ ...fieldStyle, marginTop:5 }}/></label>
        <label style={{ fontSize:11, fontWeight:800, color:'#6B7280' }}>Invoice date<input type="date" value={invoiceDate} onChange={event => onInvoiceDateChange(event.target.value)} style={{ ...fieldStyle, marginTop:5 }}/></label>
      </div>
      <CommercialInvoiceShipmentFields order={order} shipment={shipment} onShipmentChange={onShipmentChange}/>
      <div style={{ border:'1px solid #E5E7EB', borderRadius:9, overflow:'hidden' }}>
        <div style={{ padding:'10px 12px', background:'#F8FAFC', borderBottom:'1px solid #E5E7EB' }}>
          <div style={{ fontSize:12, fontWeight:900, color:'#1A1A2E' }}>Customs items</div>
          <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>Defaults come from Products. Changes here apply only to this invoice.</div>
        </div>
        <div style={{ display:'grid' }}>
          {shipment.items.map((item, index) => (
            <div key={item.key || `${item.name}-${index}`} style={{ padding:'11px 12px', borderTop:index ? '1px solid #F3F4F6' : 'none', display:'grid', gap:9 }}>
              <div style={{ display:'flex', justifyContent:'space-between', gap:12, alignItems:'baseline' }}>
                <div style={{ fontSize:12, fontWeight:900, color:'#374151' }}>{item.name}</div>
                <div style={{ fontSize:11, color:'#9CA3AF' }}>{item.sku ? `SKU ${item.sku} · ` : ''}Quantity {item.quantity}</div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:8 }}>
                <input aria-label={`Customs description for ${item.name}`} value={item.description} onChange={event => onShipmentItemChange(index, 'description', event.target.value)} placeholder="Specific English description" style={fieldStyle}/>
                <input aria-label={`HS code for ${item.name}`} value={item.hsCode} onChange={event => onShipmentItemChange(index, 'hsCode', event.target.value.replace(/\D/g, '').slice(0, 12))} inputMode="numeric" placeholder="HS code" style={fieldStyle}/>
                <input aria-label={`Country of origin for ${item.name}`} value={item.origin} onChange={event => onShipmentItemChange(index, 'origin', event.target.value.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase())} maxLength={2} placeholder="Origin" style={{ ...fieldStyle, textTransform:'uppercase' }}/>
                <input aria-label={`Unit value for ${item.name}`} type="number" min="0" step="0.01" value={item.unitValue} onChange={event => onShipmentItemChange(index, 'unitValue', event.target.value)} placeholder={`Unit ${String(order.currency || 'usd').toUpperCase()}`} style={fieldStyle}/>
                <input aria-label={`Unit net weight for ${item.name}`} type="number" min="0.001" step="0.001" value={item.netWeightKg} onChange={event => onShipmentItemChange(index, 'netWeightKg', event.target.value)} placeholder="Net kg/unit" style={fieldStyle}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const DIAL_CODE_BY_COUNTRY = {
  US:'1', CA:'1', GB:'44', IE:'353', FR:'33', DE:'49', ES:'34', IT:'39', PT:'351', NL:'31',
  BE:'32', LU:'352', CH:'41', AT:'43', SE:'46', NO:'47', DK:'45', FI:'358', IS:'354', PL:'48',
  CZ:'420', SK:'421', HU:'36', RO:'40', BG:'359', GR:'30', TR:'90', RU:'7', UA:'380', BY:'375',
  LT:'370', LV:'371', EE:'372', HR:'385', SI:'386', RS:'381', BA:'387', MK:'389', ME:'382', AL:'355',
  MT:'356', CY:'357', LI:'423', MC:'377', AD:'376', SM:'378', VA:'379',
  AU:'61', NZ:'64', JP:'81', CN:'86', HK:'852', MO:'853', TW:'886', KR:'82', KP:'850', IN:'91',
  PK:'92', BD:'880', LK:'94', NP:'977', BT:'975', MV:'960', MM:'95', TH:'66', VN:'84', LA:'856',
  KH:'855', MY:'60', SG:'65', ID:'62', PH:'63', BN:'673', TL:'670',
  IL:'972', PS:'970', JO:'962', LB:'961', SY:'963', IQ:'964', IR:'98', SA:'966', YE:'967', OM:'968',
  AE:'971', QA:'974', BH:'973', KW:'965', EG:'20', LY:'218', TN:'216', DZ:'213', MA:'212', SD:'249',
  SS:'211', ET:'251', ER:'291', DJ:'253', SO:'252', KE:'254', TZ:'255', UG:'256', RW:'250', BI:'257',
  CD:'243', CG:'242', CF:'236', CM:'237', TD:'235', NE:'227', NG:'234', BJ:'229', TG:'228', GH:'233',
  CI:'225', LR:'231', SL:'232', GN:'224', GW:'245', SN:'221', GM:'220', ML:'223', BF:'226', MR:'222',
  CV:'238', GA:'241', GQ:'240', ST:'239', AO:'244', ZM:'260', ZW:'263', MW:'265', MZ:'258', MG:'261',
  KM:'269', SC:'248', MU:'230', ZA:'27', NA:'264', BW:'267', LS:'266', SZ:'268',
  MX:'52', GT:'502', BZ:'501', SV:'503', HN:'504', NI:'505', CR:'506', PA:'507', CU:'53', JM:'1876',
  HT:'509', DO:'1809', PR:'1787', BS:'1242', BB:'1246', TT:'1868',
  CO:'57', VE:'58', GY:'592', SR:'597', EC:'593', PE:'51', BR:'55', BO:'591', PY:'595', UY:'598',
  AR:'54', CL:'56', FK:'500',
  KZ:'7', UZ:'998', TM:'993', TJ:'992', KG:'996', AF:'93', MN:'976', GE:'995', AM:'374', AZ:'994',
  FJ:'679', PG:'675', SB:'677', VU:'678', NC:'687', PF:'689', WS:'685', TO:'676', KI:'686', TV:'688',
  NR:'674', PW:'680', FM:'691', MH:'692',
};
const KNOWN_DIAL_CODES = [...new Set(Object.values(DIAL_CODE_BY_COUNTRY))].sort((a, b) => b.length - a.length);
const groupPhoneDigits = digits => {
  const groups = [];
  for (let i = 0; i < digits.length; i += 3) groups.push(digits.slice(i, i + 3));
  if (groups.length > 1 && groups[groups.length - 1].length === 1) groups[groups.length - 2] += groups.pop();
  return groups.join(' ');
};
// Normalizes any stored phone value ("+385918903417", "098228316", "06 38256977",
// "+1 301-275-2818") into { code:'+385', rest:'91 890 3417' } using the order's
// country to infer a missing dial code. Falls back to the bare digits when the
// dial code cannot be determined.
const splitPhoneByCountry = (phone, country) => {
  const raw = String(phone || '').trim();
  if (!raw) return { code:'', rest:'' };
  const countryDial = DIAL_CODE_BY_COUNTRY[String(country || '').trim().toUpperCase()] || '';
  let digits = raw.replace(/[^\d+]/g, '');
  const hasPlus = digits.startsWith('+');
  digits = digits.replace(/\D/g, '');
  if (!digits) return { code:'', rest:raw };
  let dial = '';
  let international = hasPlus;
  if (!hasPlus && digits.startsWith('00')) { digits = digits.slice(2); international = true; }
  if (international) {
    // International form: peel the dial code off the front.
    dial = countryDial && digits.startsWith(countryDial)
      ? countryDial
      : KNOWN_DIAL_CODES.find(code => digits.startsWith(code)) || '';
    if (dial) digits = digits.slice(dial.length);
  } else if (countryDial) {
    dial = countryDial;
    if (digits.startsWith(countryDial) && digits.length - countryDial.length >= 7) {
      digits = digits.slice(countryDial.length);
    } else {
      digits = digits.replace(/^0/, '');
    }
  }
  return { code: dial ? `+${dial}` : '', rest: groupPhoneDigits(digits) };
};
const formatPhoneDisplay = (phone, country) => {
  const { code, rest } = splitPhoneByCountry(phone, country);
  return [code, rest].filter(Boolean).join(' ');
};

const EXPORT_FIELD_DEFS = [
  { key:'order_id',      label:'Order ID',       hint:'Stable Omnifill reference', value:o => o.id },
  { key:'source_id',     label:'Source ID',      hint:'Original payment/platform ID', value:o => o.source_id || o.stripe_id || '' },
  { key:'source',        label:'Source',         hint:'Stripe, PayPal, CSV, etc.', value:o => PLATFORM_CFG[o.source]?.label || o.source || '' },
  { key:'order_date',    label:'Order Date',     hint:'ISO date from source', value:o => o.date || '' },
  { key:'first_name',    label:'First Name',     hint:'Recipient first name', value:o => o.first_name || '' },
  { key:'last_name',     label:'Last Name',      hint:'Recipient last name', value:o => o.last_name || '' },
  { key:'full_name',     label:'Full Name',      hint:'Recipient full name', value:o => [o.first_name, o.last_name].filter(Boolean).join(' ') },
  { key:'email',         label:'Email',          hint:'Customer email', value:o => o.email || '' },
  { key:'phone',         label:'Phone',          hint:'Customer phone', value:o => formatPhoneDisplay(o.phone, o.country) },
  { key:'company',       label:'Company',        hint:'Recipient company, if captured', value:o => o.company || '' },
  { key:'address_line1', label:'Address Line 1', hint:'Street address', value:o => o.address_line1 || '' },
  { key:'address_line2', label:'Address Line 2', hint:'Apartment, suite, or second address line', value:o => o.address_line2 || '' },
  { key:'city',          label:'City',           hint:'City/locality', value:o => cleanCity(o.city) || '' },
  { key:'state',         label:'State / Province', hint:'Region, state, county, or province', value:o => o.state || '' },
  { key:'postal_code',   label:'Postal Code',    hint:'ZIP/postcode', value:o => o.postal_code || '' },
  { key:'country',       label:'Country',        hint:'Prefer ISO alpha-2 where possible', value:o => (o.country || '').toUpperCase() },
  { key:'sku',           label:'SKU',            hint:'Product SKU from catalog', value:o => o.product_sku || o.item_sku || '' },
  { key:'product_name',  label:'Product Name',   hint:'Defined product or source product', value:o => o.product_name || normalizeProductName(o.item_raw_name || o.product) || '' },
  { key:'quantity',      label:'Quantity',       hint:'Units to fulfill', value:o => o.item_quantity || o.qty || 1 },
  { key:'shipping_method', label:'Shipping Method', hint:'Requested carrier/service when available', value:o => o.shipping_method || '' },
  { key:'amount',        label:'Order Amount',   hint:'Payment amount', value:o => o.amount || '' },
  { key:'currency',      label:'Currency',       hint:'Payment currency', value:o => (o.currency || '').toUpperCase() },
  { key:'status',        label:'Omnifill Status', hint:'Internal order status', value:o => o.status || '' },
  { key:'blank',         label:'Blank Column',   hint:'Empty placeholder column for partner templates', value:() => '' },
];

const FIELD_LABEL_BY_KEY = Object.fromEntries(EXPORT_FIELD_DEFS.map(f => [f.key, f.label]));

const EXPORT_PROFILES = [
  {
    id:'generic_3pl',
    name:'Generic 3PL CSV',
    partner:'Any warehouse',
    mode:'CSV upload',
    region:'All regions',
    cadence:'Manual download',
    required:['order_id','full_name','address_line1','city','country','product_name','quantity'],
    mapping:[
      ['OrderNumber','order_id'], ['RecipientName','full_name'], ['Email','email'], ['Phone','phone'],
      ['Address1','address_line1'], ['City','city'], ['PostalCode','postal_code'], ['Country','country'],
      ['SKU','sku'], ['ItemName','product_name'], ['Quantity','quantity'], ['Currency','currency']
    ],
  },
  {
    id:'shipstation_csv',
    name:'ShipStation CSV',
    partner:'Shipping aggregator',
    mode:'CSV upload',
    region:'Label workflow',
    cadence:'Per shipping batch',
    required:['order_id','full_name','address_line1','city','country'],
    mapping:[
      ['Order #','order_id'], ['Order Date','order_date'], ['Recipient','full_name'], ['Recipient Email','email'],
      ['Recipient Phone','phone'], ['Recipient Company','company'], ['Address 1','address_line1'], ['Address 2','address_line2'],
      ['City','city'], ['State','state'], ['Postal Code','postal_code'], ['Country','country'],
      ['SKU','sku'], ['Item Name','product_name'], ['Quantity','quantity']
    ],
  },
  {
    id:'shiphero_wms_prep',
    name:'ShipHero WMS Prep',
    partner:'ShipHero',
    mode:'CSV/WMS prep',
    region:'3PL/WMS portals',
    cadence:'Per fulfillment wave',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['order_number','order_id'], ['email','email'], ['name','full_name'], ['company','company'],
      ['address1','address_line1'], ['address2','address_line2'], ['city','city'], ['state','state'],
      ['zip','postal_code'], ['country','country'], ['phone','phone'], ['sku','sku'],
      ['product_name','product_name'], ['quantity','quantity'], ['shipping_method','shipping_method']
    ],
  },
  {
    id:'shipmonk_intake_csv',
    name:'ShipMonk Intake CSV',
    partner:'ShipMonk',
    mode:'CSV/API-style upload',
    region:'Multi-warehouse',
    cadence:'Per fulfillment wave',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['order_key','order_id'], ['recipient.name','full_name'], ['recipient.email','email'], ['recipient.phone','phone'],
      ['recipient.address.address1','address_line1'], ['recipient.address.address2','address_line2'], ['recipient.address.city','city'],
      ['recipient.address.state','state'], ['recipient.address.zip','postal_code'], ['recipient.address.country','country'],
      ['items.sku','sku'], ['items.name','product_name'], ['items.quantity','quantity']
    ],
  },
  {
    id:'red_stag_prep',
    name:'Red Stag Prep',
    partner:'Red Stag Fulfillment',
    mode:'CSV intake prep',
    region:'US fulfillment',
    cadence:'Per fulfillment batch',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['Order Number','order_id'], ['Recipient Name','full_name'], ['Email','email'], ['Phone','phone'],
      ['Company','company'], ['Address 1','address_line1'], ['Address 2','address_line2'], ['City','city'],
      ['State','state'], ['Postal Code','postal_code'], ['Country','country'], ['SKU','sku'],
      ['Item Name','product_name'], ['Qty','quantity'], ['Shipping Method','shipping_method']
    ],
  },
  {
    id:'shipnetwork_prep',
    name:'ShipNetwork Prep',
    partner:'ShipNetwork',
    mode:'CSV intake prep',
    region:'US fulfillment',
    cadence:'Per fulfillment batch',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['Order ID','order_id'], ['Customer Name','full_name'], ['Customer Email','email'], ['Phone','phone'],
      ['Address 1','address_line1'], ['Address 2','address_line2'], ['City','city'], ['State','state'],
      ['Zip','postal_code'], ['Country','country'], ['SKU','sku'], ['Description','product_name'],
      ['Quantity','quantity'], ['Carrier Service','shipping_method']
    ],
  },
  {
    id:'qml_board_game_csv',
    name:'QML Board Game CSV',
    partner:'Quartermaster Logistics',
    mode:'CSV upload',
    region:'US/Canada tabletop',
    cadence:'Campaign fulfillment wave',
    required:['order_id','email','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['Order ID','order_id'], ['Email','email'], ['First Name','first_name'], ['Last Name','last_name'],
      ['Name','full_name'], ['Phone','phone'], ['Address 1','address_line1'], ['Address 2','address_line2'],
      ['City','city'], ['State/Province','state'], ['Postal Code','postal_code'], ['Country','country'],
      ['SKU','sku'], ['Product Name','product_name'], ['Quantity','quantity']
    ],
  },
  {
    id:'fulfillrite_csv',
    name:'Fulfillrite CSV',
    partner:'Fulfillrite',
    mode:'CSV upload',
    region:'US fulfillment',
    cadence:'Per fulfillment batch',
    required:['order_id','first_name','last_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['Order Number','order_id'], ['First Name','first_name'], ['Last Name','last_name'], ['Email','email'],
      ['Phone','phone'], ['Address 1','address_line1'], ['Address 2','address_line2'], ['City','city'],
      ['State','state'], ['Zip','postal_code'], ['Country','country'], ['SKU','sku'],
      ['Product','product_name'], ['Quantity','quantity']
    ],
  },
  {
    id:'efulfillment_service_csv',
    name:'eFulfillment Service CSV',
    partner:'eFulfillment Service',
    mode:'CSV upload',
    region:'US fulfillment',
    cadence:'Daily or batch upload',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['Order Number','order_id'], ['Customer Name','full_name'], ['Email','email'], ['Phone','phone'],
      ['Address 1','address_line1'], ['Address 2','address_line2'], ['City','city'], ['State','state'],
      ['Zip','postal_code'], ['Country','country'], ['SKU','sku'], ['Item Description','product_name'],
      ['Quantity','quantity'], ['Shipping Method','shipping_method']
    ],
  },
  {
    id:'gamesquest_shipquest_csv',
    name:'GamesQuest / ShipQuest CSV',
    partner:'GamesQuest',
    mode:'CSV/XLS hub export',
    region:'UK/EU tabletop',
    cadence:'Campaign fulfillment wave',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['Order Number','order_id'], ['Name','full_name'], ['Email','email'], ['Phone','phone'],
      ['Address Line 1','address_line1'], ['Address Line 2','address_line2'], ['City','city'], ['County/State','state'],
      ['Postcode','postal_code'], ['Country Code','country'], ['SKU','sku'], ['Item Name','product_name'], ['Quantity','quantity']
    ],
  },
  {
    id:'spiral_galaxy_csv',
    name:'Spiral Galaxy CSV',
    partner:'Spiral Galaxy Games',
    mode:'CSV/XLS hub export',
    region:'UK/EU tabletop',
    cadence:'Campaign fulfillment wave',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['Order ID','order_id'], ['Recipient Name','full_name'], ['Email','email'], ['Phone','phone'],
      ['Address 1','address_line1'], ['Address 2','address_line2'], ['Town/City','city'], ['County','state'],
      ['Postcode','postal_code'], ['Country','country'], ['Product SKU','sku'], ['Product Name','product_name'], ['Qty','quantity']
    ],
  },
  {
    id:'shippo_csv',
    name:'Shippo Upload',
    partner:'Shipping aggregator',
    mode:'CSV upload',
    region:'Label workflow',
    cadence:'Per label batch',
    required:['full_name','address_line1','city','postal_code','country'],
    mapping:[
      ['Name','full_name'], ['Company','source'], ['Street1','address_line1'], ['City','city'],
      ['Zip','postal_code'], ['Country','country'], ['Phone','phone'], ['Email','email'],
      ['OrderId','order_id'], ['SKU','sku'], ['Quantity','quantity']
    ],
  },
  {
    id:'easyship_csv',
    name:'Easyship Upload',
    partner:'Shipping aggregator',
    mode:'CSV upload',
    region:'International labels',
    cadence:'Per fulfillment wave',
    required:['order_id','full_name','address_line1','city','country','product_name','quantity'],
    mapping:[
      ['Order Number','order_id'], ['Receiver Name','full_name'], ['Receiver Email','email'], ['Receiver Phone','phone'],
      ['Address Line 1','address_line1'], ['City','city'], ['Postal Code','postal_code'], ['Country','country'],
      ['Item SKU','sku'], ['Item Description','product_name'], ['Item Quantity','quantity'], ['Currency','currency']
    ],
  },
  {
    id:'amazon_mcf_prep',
    name:'Amazon MCF Prep',
    partner:'Amazon Multi-Channel Fulfillment',
    mode:'CSV/API prep',
    region:'Marketplace fulfillment',
    cadence:'Per fulfillment batch',
    required:['order_id','full_name','address_line1','city','postal_code','country','sku','quantity'],
    mapping:[
      ['MerchantFulfillmentOrderId','order_id'], ['DisplayableOrderId','source_id'], ['DisplayableOrderDate','order_date'],
      ['DisplayableOrderComment','product_name'], ['FulfillmentAction','blank'], ['ShipToName','full_name'],
      ['ShipToAddressLine1','address_line1'], ['ShipToAddressLine2','address_line2'], ['ShipToCity','city'],
      ['ShipToStateOrRegion','state'], ['ShipToPostalCode','postal_code'], ['ShipToCountryCode','country'],
      ['ShipToPhone','phone'], ['SellerSku','sku'], ['Quantity','quantity']
    ],
  },
  {
    id:'shipbob_api_shape',
    name:'ShipBob API Prep',
    partner:'3PL/WMS',
    mode:'API shape preview',
    region:'Warehouse routing',
    cadence:'Backend integration needed',
    required:['order_id','full_name','address_line1','city','country','sku','quantity'],
    mapping:[
      ['reference_id','order_id'], ['recipient.name','full_name'], ['recipient.email','email'],
      ['recipient.address.address1','address_line1'], ['recipient.address.city','city'],
      ['recipient.address.zip_code','postal_code'], ['recipient.address.country','country'],
      ['products.reference_id','sku'], ['products.name','product_name'], ['products.quantity','quantity']
    ],
  },
];

const EXPORT_PROFILE_BRANDING = {
  generic_3pl: { initials:'3PL', color:'#006FDD', bg:'#EEF6FF' },
  shipstation_csv: { logo:'/assets/fulfillment/shipstation.webp', initials:'SS', color:'#2563EB', bg:'#EFF6FF' },
  shiphero_wms_prep: { logo:'/assets/fulfillment/shiphero.webp', initials:'SH', color:'#2563EB', bg:'#EFF6FF' },
  shipmonk_intake_csv: { logo:'/assets/fulfillment/shipmonk.webp', initials:'SM', color:'#7C3AED', bg:'#F5F3FF' },
  red_stag_prep: { logo:'/assets/fulfillment/red_stag.webp', initials:'RS', color:'#991B1B', bg:'#FEF2F2' },
  shipnetwork_prep: { logo:'/assets/fulfillment/shipnetwork.webp', initials:'SN', color:'#0F766E', bg:'#ECFDF5' },
  qml_board_game_csv: { logo:'/assets/fulfillment/qml-logo-alt-1.webp', initials:'QML', color:'#A16207', bg:'#FEF3C7' },
  fulfillrite_csv: { logo:'/assets/fulfillment/fulfillrite.webp', initials:'FR', color:'#059669', bg:'#ECFDF5' },
  efulfillment_service_csv: { logo:'/assets/fulfillment/efulfillment.webp', initials:'EF', color:'#475569', bg:'#F1F5F9' },
  gamesquest_shipquest_csv: { logo:'/assets/fulfillment/gamesquest.webp', initials:'GQ', color:'#BE123C', bg:'#FFF1F2' },
  spiral_galaxy_csv: { logo:'/assets/fulfillment/spiral_galaxy.webp', initials:'SG', color:'#4338CA', bg:'#EEF2FF' },
  shippo_csv: { logo:'/assets/fulfillment/shippo.png', initials:'SP', color:'#006FDD', bg:'#EEF6FF' },
  easyship_csv: { logo:'/assets/fulfillment/easyship.webp', initials:'ES', color:'#0F766E', bg:'#ECFDF5' },
  amazon_mcf_prep: { logo:'/assets/fulfillment/amazon_mcf.webp', initials:'AMZ', color:'#111827', bg:'#FFF7ED' },
  shipbob_api_shape: { logo:'/assets/fulfillment/shipbob.webp', initials:'SB', color:'#EA580C', bg:'#FFF7ED' },
};

const DATE_FORMATS = [
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
  { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

const formatDate = (iso, fmt = 'DD-MM-YYYY') => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  switch (fmt) {
    case 'DD-MM-YYYY': return `${d}-${m}-${y}`;
    case 'MM-DD-YYYY': return `${m}-${d}-${y}`;
    default:           return iso;
  }
};
const isPlaceholderValue = value => ['n/a', 'na', 'not applicable', 'none', 'null', 'undefined'].includes(String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase());
const cleanCity = c => c && !isPlaceholderValue(c) ? c.replace(/\s+\d+$/, '').trim() : '';
const cleanLocationPart = value => {
  const part = String(value ?? '').trim();
  return part && !isPlaceholderValue(part) ? part : '';
};
const EDITED_ORDER_COMPARE_FIELDS = [
  'date', 'first_name', 'last_name', 'email', 'phone', 'address_line1',
  'city', 'state', 'postal_code', 'country', 'product', 'product_id', 'qty',
  'amount', 'currency',
];
const comparableOrderValue = value => value === null || value === undefined ? '' : String(value).trim();
const isEditedOrder = order => {
  if (!order?.original_order_json) return false;
  try {
    const original = JSON.parse(order.original_order_json);
    return EDITED_ORDER_COMPARE_FIELDS.some(field => comparableOrderValue(order[field]) !== comparableOrderValue(original[field]));
  } catch {
    return true;
  }
};

// ─── API HELPER ────────────────────────────────────────────────
const api = async (method, path, body, options = {}) => {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    credentials: 'same-origin',
  };
  if (options.signal) opts.signal = options.signal;
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(path, opts);
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};

let authenticatorQrLoadPromise = null;
const ensureAuthenticatorQrGenerator = () => {
  if (typeof window.qrcode === 'function' || window.QRCode?.toDataURL) return Promise.resolve();
  if (!authenticatorQrLoadPromise) {
    authenticatorQrLoadPromise = loadExternalScript('qrcode-generator-loader', '/assets/vendor/qrcode-generator/qrcode-1.4.4.min.js?v=20260519-local-icons')
      .catch(error => {
        authenticatorQrLoadPromise = null;
        throw error;
      });
  }
  return authenticatorQrLoadPromise;
};

const buildAuthenticatorQrDataUrl = async otpauthUrl => {
  await ensureAuthenticatorQrGenerator();

  const qrFactory = window.qrcode;
  if (typeof qrFactory === 'function') {
    const qr = qrFactory(0, 'M');
    qr.addData(otpauthUrl);
    qr.make();
    return qr.createDataURL(6, 2);
  }

  const legacyGenerator = window.QRCode;
  if (legacyGenerator?.toDataURL) {
    try {
      const maybePromise = legacyGenerator.toDataURL(otpauthUrl, { errorCorrectionLevel:'M', margin:2, width:260 });
      if (typeof maybePromise === 'string') return maybePromise;
      if (maybePromise?.then) return maybePromise;
    } catch {}
    return new Promise((resolve, reject) => {
      legacyGenerator.toDataURL(otpauthUrl, { errorCorrectionLevel:'M', margin:2, width:260 }, (err, url) => {
        if (err || !url) reject(err || new Error('QR code render failed'));
        else resolve(url);
      });
    });
  }

  return Promise.reject(new Error('QR code generator unavailable'));
};

const TwoFactorModal = ({
  open, status, setup, setupCode, setSetupCode, showManualKey, setShowManualKey,
  actionCode, setActionCode, recoveryCode, setRecoveryCode, useRecovery, setUseRecovery,
  newRecoveryCodes, loading, error, notice, onClose, onStartSetup, onConfirmSetup,
  onRegenerateRecoveryCodes, onDisableTwoFactor, onCopyText,
}) => {
  const [qrSrc, setQrSrc] = useState('');
  const [qrError, setQrError] = useState('');

  useEffect(() => {
    if (!open || !setup?.otpauthUrl) {
      setQrSrc('');
      setQrError('');
      return undefined;
    }
    let active = true;
    setQrSrc('');
    setQrError('');
    buildAuthenticatorQrDataUrl(setup.otpauthUrl)
      .then(url => { if (active) setQrSrc(url); })
      .catch(() => { if (active) setQrError('Could not render the QR code. Use the setup key instead.'); });
    return () => { active = false; };
  }, [open, setup?.otpauthUrl]);

  if (!open) return null;

  const inputStyle = { width:'100%', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 10px', fontSize:13, outline:'none', background:'#fff', color:'#111827' };
  const labelStyle = { display:'block', fontSize:12, fontWeight:800, color:'#374151', marginBottom:6 };
  const primaryButtonStyle = { background:'#006FDD', color:'#fff', border:'1px solid #006FDD', borderRadius:8, padding:'9px 12px', fontSize:13, fontWeight:900, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:7 };
  const secondaryButtonStyle = { background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 12px', fontSize:13, fontWeight:900, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:7 };
  const dangerButtonStyle = { ...secondaryButtonStyle, color:'#B91C1C', borderColor:'#FECACA', background:'#FEF2F2' };
  const disabled = !!loading;
  const isEnabled = !!status.enabled;
  const showingRecoveryCodes = newRecoveryCodes.length > 0;
  const helperText = showingRecoveryCodes
    ? 'Save these recovery codes now. They are shown once.'
    : isEnabled
    ? '2FA is on. Enter a current code before changing recovery codes or disabling it.'
    : setup
      ? 'Next: scan the QR code with Google Authenticator, then enter the 6-digit code.'
      : 'Google Authenticator is recommended. Start setup to scan a QR code.';
  const manageActionRowStyle = { display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap:8, alignItems:'stretch' };
  const manageActionButtonStyle = { width:'100%', minWidth:0, minHeight:38, padding:'8px 8px', whiteSpace:'normal', lineHeight:1.2 };

  return (
    <div role="dialog" aria-modal="true" aria-label="2-Factor Authentication (2FA)" onClick={showingRecoveryCodes ? undefined : onClose} style={{ position:'fixed', inset:0, zIndex:10000, background:'#F9FAFB', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:520, maxHeight:'calc(100vh - 36px)', overflowY:'auto', background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'none', padding:16, display:'grid', gap:14 }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
          <div>
            <h3 style={{ margin:0, color:'#111827', fontSize:15, fontWeight:900 }}>2-Factor Authentication (2FA)</h3>
            <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:12, lineHeight:1.45 }}>{helperText}</p>
          </div>
          {!showingRecoveryCodes && (
            <button type="button" onClick={onClose} aria-label="Close" style={{ width:30, height:30, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:8, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}><X size={15}/></button>
          )}
        </div>

        {notice && <div style={{ background:'#EEF6FF', border:'1px solid #BFDBFE', borderRadius:8, color:'#1D4ED8', padding:'10px 12px', fontSize:13, fontWeight:700 }}>{notice}</div>}
        {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, color:'#991B1B', padding:'10px 12px', fontSize:13, fontWeight:700 }}>{error}</div>}

        {showingRecoveryCodes ? (
          <div style={{ display:'grid', gap:13 }}>
            <div style={{ border:'1px solid #E5E7EB', background:'#fff', borderRadius:8, padding:13, display:'grid', gap:10 }}>
              <div style={{ color:'#111827', fontSize:13, fontWeight:900 }}>Recovery codes</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:8 }}>
                {newRecoveryCodes.map(code => (
                  <code key={code} style={{ border:'1px solid #E5E7EB', background:'#fff', borderRadius:7, padding:'8px 9px', color:'#111827', fontSize:12, fontWeight:900, textAlign:'center', letterSpacing:'0.08em' }}>{code}</code>
                ))}
              </div>
            </div>
            <button type="button" onClick={onClose} style={{ ...primaryButtonStyle, justifySelf:'start' }}>Return</button>
          </div>
        ) : !isEnabled && !setup && (
          <div style={{ display:'grid', gap:12 }}>
            <div style={{ color:'#6B7280', fontSize:13, lineHeight:1.5 }}>Use Google Authenticator or another app that creates 6-digit codes.</div>
            <button type="button" onClick={onStartSetup} disabled={disabled} style={{ ...primaryButtonStyle, cursor:disabled ? 'wait' : 'pointer' }}>{loading === 'setup' ? 'Preparing...' : 'Start setup'}</button>
          </div>
        )}

        {!showingRecoveryCodes && !isEnabled && setup && (
          <div style={{ display:'grid', gap:13 }}>
            <div style={{ border:'1px solid #E5E7EB', background:'#fff', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', padding:12, minHeight:284 }}>
              {qrSrc ? <img src={qrSrc} alt="Authenticator setup QR code" style={{ width:260, height:260, imageRendering:'pixelated' }}/> : <div style={{ color:qrError ? '#B91C1C' : '#6B7280', fontSize:13, fontWeight:800, textAlign:'center', lineHeight:1.45 }}>{qrError || 'Preparing QR code...'}</div>}
            </div>
            <div style={{ display:'grid', gap:8 }}>
              <a href={setup.otpauthUrl} style={{ color:'#006FDD', fontSize:13, fontWeight:800, textDecoration:'none', textAlign:'center' }}>Open setup link</a>
              <button type="button" onClick={() => setShowManualKey(value => !value)} style={secondaryButtonStyle}>{showManualKey ? 'Hide setup key' : 'Use setup key instead'}</button>
            </div>
            {showManualKey && (
              <div>
                <label style={labelStyle}>Setup key</label>
                <div style={{ display:'flex', gap:8, alignItems:'stretch' }}>
                  <code style={{ flex:1, minWidth:0, border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 10px', background:'#fff', color:'#111827', fontSize:13, fontWeight:900, letterSpacing:'0.08em', overflowWrap:'anywhere' }}>{setup.secret}</code>
                  <button type="button" onClick={() => onCopyText(setup.secret)} style={secondaryButtonStyle}><Copy size={14}/>Copy</button>
                </div>
              </div>
            )}
            <div>
              <label style={labelStyle}>6-digit code</label>
              <input value={setupCode} onChange={e => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" style={{ ...inputStyle, textAlign:'center', letterSpacing:'0.16em', fontWeight:900 }}/>
            </div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button type="button" onClick={onConfirmSetup} disabled={disabled || setupCode.length !== 6} style={{ ...primaryButtonStyle, opacity:disabled || setupCode.length !== 6 ? 0.62 : 1, cursor:disabled ? 'wait' : setupCode.length === 6 ? 'pointer' : 'not-allowed' }}>{loading === 'confirm' ? 'Verifying...' : 'Verify and enable'}</button>
              <button type="button" onClick={onClose} disabled={disabled} style={secondaryButtonStyle}>Cancel</button>
            </div>
          </div>
        )}

        {!showingRecoveryCodes && isEnabled && (
          <div style={{ display:'grid', gap:13 }}>
            <div style={{ color:'#6B7280', fontSize:13, lineHeight:1.5 }}>Recovery codes remaining: <strong style={{ color:'#111827' }}>{status.recoveryCodesRemaining ?? 0}</strong></div>
            {useRecovery ? (
              <div>
                <label style={labelStyle}>Recovery code</label>
                <input value={recoveryCode} onChange={e => setRecoveryCode(e.target.value.toUpperCase())} autoComplete="one-time-code" placeholder="ABCD-EFGH" style={{ ...inputStyle, textAlign:'center', letterSpacing:'0.08em', fontWeight:900 }}/>
              </div>
            ) : (
              <div>
                <label style={labelStyle}>Current 6-digit code</label>
                <input value={actionCode} onChange={e => setActionCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" style={{ ...inputStyle, textAlign:'center', letterSpacing:'0.16em', fontWeight:900 }}/>
              </div>
            )}
            <div style={manageActionRowStyle}>
              <button type="button" onClick={onRegenerateRecoveryCodes} disabled={disabled} style={{ ...secondaryButtonStyle, ...manageActionButtonStyle }}>{loading === 'recovery' ? 'Working...' : 'Create recovery codes'}</button>
              <button type="button" onClick={onDisableTwoFactor} disabled={disabled} style={{ ...dangerButtonStyle, ...manageActionButtonStyle }}>{loading === 'disable' ? 'Disabling...' : 'Disable 2FA'}</button>
              <button type="button" onClick={() => setUseRecovery(value => !value)} disabled={disabled} style={{ ...secondaryButtonStyle, ...manageActionButtonStyle, color:'#006FDD' }}>{useRecovery ? 'Use authenticator code instead' : 'Use recovery code instead'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TwoFactorSettingsPanel = ({ user, onUserChange, apiClient = api }) => {
  const [status, setStatus] = useState({
    enabled: !!user?.twoFactorEnabled,
    recoveryCodesRemaining: user?.recoveryCodesRemaining || 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [setup, setSetup] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [showManualKey, setShowManualKey] = useState(false);
  const [actionCode, setActionCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadStatus = useCallback(async () => {
    const { ok, data } = await apiClient('GET', '/api/auth/2fa/status');
    if (ok) setStatus(data);
  }, [apiClient]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const refreshUser = async () => {
    await loadStatus();
    await onUserChange?.();
  };

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  const openManage = () => {
    clearMessages();
    setNewRecoveryCodes([]);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (loading) return;
    setModalOpen(false);
    setSetup(null);
    setSetupCode('');
    setShowManualKey(false);
    setActionCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setNewRecoveryCodes([]);
    clearMessages();
  };

  const beginSetup = async () => {
    clearMessages();
    setModalOpen(true);
    setSetup(null);
    setSetupCode('');
    setShowManualKey(false);
    setNewRecoveryCodes([]);
    setLoading('setup');
    const { ok, data } = await apiClient('POST', '/api/auth/2fa/setup', {});
    setLoading('');
    if (!ok) return setError(data?.error || 'Could not start 2FA setup.');
    setSetup(data);
  };

  const confirmSetup = async () => {
    clearMessages();
    if (setupCode.length !== 6) return setError('Enter the 6-digit authenticator code.');
    setLoading('confirm');
    const { ok, data } = await apiClient('POST', '/api/auth/2fa/confirm', { code: setupCode });
    setLoading('');
    if (!ok) return setError(data?.error || 'Could not enable 2FA.');
    setSetup(null);
    setSetupCode('');
    setShowManualKey(false);
    setNewRecoveryCodes(data?.recoveryCodes || []);
    setStatus({ enabled:true, recoveryCodesRemaining:data?.recoveryCodesRemaining || data?.recoveryCodes?.length || 0 });
    setNotice('2FA is enabled for this account across all Omnifill portals. Save these recovery codes now.');
    await onUserChange?.();
  };

  const verificationPayload = () => useRecovery
    ? { recoveryCode }
    : { code: actionCode.replace(/\D/g, '') };

  const requireVerificationInput = () => {
    if (useRecovery && !recoveryCode.trim()) return 'Enter a recovery code.';
    if (!useRecovery && actionCode.replace(/\D/g, '').length !== 6) return 'Enter the 6-digit authenticator code.';
    return '';
  };

  const regenerateRecoveryCodes = async () => {
    clearMessages();
    const inputError = requireVerificationInput();
    if (inputError) return setError(inputError);
    setLoading('recovery');
    const { ok, data } = await apiClient('POST', '/api/auth/2fa/recovery-codes', verificationPayload());
    setLoading('');
    if (!ok) return setError(data?.error || 'Could not create recovery codes.');
    setActionCode('');
    setRecoveryCode('');
    setNewRecoveryCodes(data?.recoveryCodes || []);
    setStatus(prev => ({ ...prev, recoveryCodesRemaining:data?.recoveryCodesRemaining || data?.recoveryCodes?.length || 0 }));
    setNotice('New recovery codes are ready. Older recovery codes no longer work.');
    await refreshUser();
  };

  const disableTwoFactor = async () => {
    clearMessages();
    const inputError = requireVerificationInput();
    if (inputError) return setError(inputError);
    setLoading('disable');
    const { ok, data } = await apiClient('POST', '/api/auth/2fa/disable', verificationPayload());
    setLoading('');
    if (!ok) return setError(data?.error || 'Could not disable 2FA.');
    setActionCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setNewRecoveryCodes([]);
    setStatus({ enabled:false, recoveryCodesRemaining:0 });
    setNotice('2FA is disabled for this account.');
    await refreshUser();
  };

  const copyText = async text => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice('Copied.');
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const primaryButtonStyle = { background:'#006FDD', color:'#fff', border:'1px solid #006FDD', borderRadius:8, padding:'9px 12px', fontSize:13, fontWeight:900, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:7 };
  const secondaryButtonStyle = { background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 12px', fontSize:13, fontWeight:900, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:7 };
  const disabled = !!loading;

  return (
    <section className="dash-panel workspace-panel-flat" style={{ overflow:'hidden' }}>
      <div className="dash-panel-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14, flexWrap:'wrap', background:'#fff' }}>
        <div>
          <h2 style={{ margin:0, color:'#111827', fontSize:16, fontWeight:900 }}>2-Factor Authentication (2FA)</h2>
          <p className="dash-page-subtitle" style={{ marginTop:5, maxWidth:650 }}>
            {status.enabled ? '2FA is on. Use Manage to change recovery codes or disable it.' : 'Google Authenticator is recommended. Next: set up 2FA.'}
          </p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ display:'inline-flex', alignItems:'center', border:'1px solid #E5E7EB', background:status.enabled ? '#F0FDF4' : '#F9FAFB', color:status.enabled ? '#065F46' : '#6B7280', borderRadius:8, padding:'5px 8px', fontSize:12, fontWeight:900 }}>
            {status.enabled ? 'Enabled' : 'Disabled'}
          </span>
          {status.enabled ? (
            <button type="button" onClick={openManage} disabled={disabled} style={secondaryButtonStyle}>Manage</button>
          ) : (
            <button type="button" onClick={beginSetup} disabled={disabled} style={{ ...primaryButtonStyle, cursor:disabled ? 'wait' : 'pointer' }}>{loading === 'setup' ? 'Preparing...' : 'Set up 2FA'}</button>
          )}
        </div>
      </div>
      <TwoFactorModal
        open={modalOpen}
        status={status}
        setup={setup}
        setupCode={setupCode}
        setSetupCode={setSetupCode}
        showManualKey={showManualKey}
        setShowManualKey={setShowManualKey}
        actionCode={actionCode}
        setActionCode={setActionCode}
        recoveryCode={recoveryCode}
        setRecoveryCode={setRecoveryCode}
        useRecovery={useRecovery}
        setUseRecovery={setUseRecovery}
        newRecoveryCodes={newRecoveryCodes}
        loading={loading}
        error={error}
        notice={notice}
        onClose={closeModal}
        onStartSetup={beginSetup}
        onConfirmSetup={confirmSetup}
        onRegenerateRecoveryCodes={regenerateRecoveryCodes}
        onDisableTwoFactor={disableTwoFactor}
        onCopyText={copyText}
      />
    </section>
  );
};

const loadExternalScript = (id, src) => new Promise((resolve, reject) => {
  if (document.getElementById(id)) {
    const existing = document.getElementById(id);
    if (existing.dataset.loaded === 'true') resolve();
    else {
      existing.addEventListener('load', () => resolve(), { once:true });
      existing.addEventListener('error', () => reject(new Error(`Could not load ${src}`)), { once:true });
    }
    return;
  }
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  script.onload = () => {
    script.dataset.loaded = 'true';
    resolve();
  };
  script.onerror = () => reject(new Error(`Could not load ${src}`));
  document.head.appendChild(script);
});

let chartJsLoadPromise = null;
const loadChartJs = () => {
  if (window.Chart) return Promise.resolve();
  if (!chartJsLoadPromise) {
    chartJsLoadPromise = loadExternalScript('chartjs-loader', '/assets/vendor/chartjs/chart-4.5.1.umd.min.js?v=20260519-local-runtime');
  }
  return chartJsLoadPromise;
};

// ─── SHARED COMPONENTS ─────────────────────────────────────────
const Logo = ({ size = 20, suffix = "" }) => (
  <div style={{ display:"flex", alignItems:"center", gap:8, userSelect:"none" }}>
    <img src="/assets/logo.webp" alt="" style={{ width: size + 8, height: size + 8, objectFit:"contain" }}/>
    <span style={{ fontSize: size, fontWeight: 800, letterSpacing: "-0.5px", lineHeight:1 }}>
      <span style={{ color:"#73D04B" }}>Omni</span>
      <span style={{ color:"#006FDD" }}>fill</span>
    </span>
    {suffix ? <span style={{ color:"#D1D5DB", fontSize:Math.max(11, size - 5), fontWeight:800, letterSpacing:0, lineHeight:1, textTransform:"lowercase" }}>{suffix}</span> : null}
  </div>
);

const DashboardLoader = () => (
  <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'radial-gradient(circle at 50% 42%, #FFFFFF 0%, #F8F9FA 52%, #EEF6FF 100%)', color:'#1A1A2E' }}>
    <div className="dashboard-loader-logo" style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:18, animation:'logoDropIn 560ms cubic-bezier(0.16, 1, 0.3, 1) both' }}>
      <div className="dashboard-loader-mark" style={{ width:132, height:132, display:'flex', alignItems:'center', justifyContent:'center', animation:'logoPulse 1800ms ease-in-out infinite' }}>
        <img src="/assets/logo-loader.webp" alt="" decoding="async" style={{ width:122, height:122, objectFit:'contain' }}/>
      </div>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
        <div style={{ fontSize:24, fontWeight:800, lineHeight:1, userSelect:'none' }}>
          <span style={{ color:'#73D04B' }}>Omni</span>
          <span style={{ color:'#006FDD' }}>fill</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:5 }} aria-label="Loading dashboard" role="status">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="dashboard-loader-dot"
              style={{ width:6, height:6, borderRadius:'50%', background:i === 1 ? '#73D04B' : '#006FDD', animation:`loadingDot 920ms ease-in-out ${i * 120}ms infinite` }}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const SkeletonPill = ({ width = 54, height = 18, style = {} }) => (
  <span
    className="skeleton-pill"
    aria-hidden="true"
    style={{
      display:'inline-block',
      width,
      height,
      borderRadius:999,
      verticalAlign:'middle',
      ...style,
    }}
  />
);

const PlatformPill = ({ source, size = 'sm', bare = false }) => {
  const cfg = PLATFORM_CFG[source] || { label: source, color:'#6B7280', bg:'#F3F4F6', char:'?' };
  const sz = size === 'sm' ? 30 : 46;
  const Icon = cfg.Icon;
  return (
    <div style={{ width:sz, height:sz, background:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      {Icon
        ? <Icon size={source === 'manual' ? (size === 'sm' ? 25 : 36) : (size === 'sm' ? 22 : 32)} strokeWidth={source === 'manual' ? 2.4 : 2.1} color={cfg.color}/>
        : cfg.logo
        ? <img src={cfg.logo} alt={cfg.label} style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
        : <span style={{ color:cfg.color, fontWeight:800, fontSize: size==='sm'?16:22, lineHeight:1 }}>{cfg.char}</span>
      }
    </div>
  );
};

const normalizeDashboardOrderStatus = status => {
  const normalized = String(status || 'paid').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized === 'pending') return 'paid';
  if (normalized === 'fulfilled') return 'routed';
  if (['ignored', 'passed', 'pass', 'skip', 'skipped', 'refund', 'refunded', 'void', 'voided', 'canceled', 'cancelled'].includes(normalized)) return 'ignored';
  return normalized;
};

const isIgnoredDashboardOrder = order => normalizeDashboardOrderStatus(order?.status) === 'ignored';


const Dot = ({ color }) => (
  <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:color, flexShrink:0 }}/>
);

const formatMoneyCompact = (value, currency = 'usd') => {
  const sym = { usd: '$', eur: '€', gbp: '£', cad: 'C$', aud: 'A$' };
  const cur = (currency || 'usd').toLowerCase();
  const prefix = sym[cur] || `${cur.toUpperCase()} `;
  const n = Number.isFinite(value) ? value : 0;
  return prefix + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CurrencyBreakdownBadge = ({ rows = [] }) => {
  const [open, setOpen] = useState(false);
  const cleanRows = rows
    .map(row => ({
      currency: String(row.currency || 'usd').toLowerCase(),
      revenue: Number(row.revenue || 0),
      count: Number(row.count || 0),
    }))
    .filter(row => row.currency && (row.revenue || row.count))
    .sort((a, b) => Math.abs(b.revenue) - Math.abs(a.revenue));

  if (cleanRows.length <= 1) return null;

  const label = cleanRows.map(row => `${formatMoneyCompact(row.revenue, row.currency)} ${row.currency.toUpperCase()}`).join(', ');

  return (
    <span
      tabIndex={0}
      title={label}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      style={{ position:'relative', zIndex: open ? 100 : 'auto', display:'inline-flex', alignItems:'center', flexShrink:0, outline:'none' }}
    >
      <span style={{ color:'#9CA3AF', fontSize:11, fontWeight:700, cursor:'help', borderBottom:'1px dotted #CBD5E1', lineHeight:1.2 }}>mixed</span>
      {open && (
        <span style={{ position:'absolute', right:0, top:'calc(100% + 8px)', zIndex:1000, minWidth:184, background:'#111827', color:'#fff', borderRadius:8, padding:'9px 10px', boxShadow:'0 12px 30px rgba(15,23,42,0.22)', pointerEvents:'none' }}>
          <span style={{ display:'block', fontSize:11, fontWeight:800, color:'#D1D5DB', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>Revenue by currency</span>
          {cleanRows.map(row => (
            <span key={row.currency} style={{ display:'flex', justifyContent:'space-between', gap:16, alignItems:'baseline', fontSize:12, lineHeight:1.55 }}>
              <span style={{ color:'#9CA3AF', fontWeight:800 }}>{row.currency.toUpperCase()}</span>
              <span style={{ color:'#fff', fontWeight:800, fontVariantNumeric:'tabular-nums' }}>{formatMoneyCompact(row.revenue, row.currency)}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

const parseOrderDate = (order) => {
  if (!order) return null;
  const date = String(order.date || '').trim();
  const created = String(order.created_at || '').trim();
  const today = new Date().toISOString().slice(0, 10);
  const raw = date === today && created ? created : (date || created);
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? `${raw}T00:00:00`
    : /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
      ? `${raw.replace(' ', 'T')}Z`
      : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseOrderSeenDate = (order) => {
  if (!order) return null;
  const created = String(order.created_at || '').trim();
  const raw = created || String(order.date || '').trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? `${raw}T00:00:00`
    : /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
      ? `${raw.replace(' ', 'T')}Z`
      : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const timeAgo = (date, now = new Date()) => {
  if (!date) return 'date unknown';
  const seconds = Math.max(0, Math.floor((now - date) / 1000));
  if (seconds < 45) return 'just now';
  const units = [
    ['year', 31536000],
    ['month', 2592000],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  const [name, size] = units.find(([, size]) => seconds >= size) || ['minute', 60];
  const count = Math.floor(seconds / size);
  return `${count} ${name}${count === 1 ? '' : 's'} ago`;
};

const ColorSelect = ({ value, onChange, options, style, triggerPadding = '7px 10px' }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const updateMenuPosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const estimatedHeight = Math.min(280, Math.max(42, options.length * 38 + 2));
    const belowTop = rect.bottom + 4;
    const aboveTop = Math.max(8, rect.top - estimatedHeight - 4);
    const hasRoomBelow = belowTop + estimatedHeight <= window.innerHeight - 8;
    const top = hasRoomBelow || rect.bottom < window.innerHeight / 2 ? belowTop : aboveTop;
    setMenuPos({
      left: rect.left,
      top,
      width: rect.width,
      maxHeight: Math.max(96, (top < rect.top ? rect.top : window.innerHeight - top) - 8),
    });
  }, [options.length]);
  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const handler = e => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);
  const sel = options.find(o => o.value === value) || options[0];
  const menu = open && menuPos ? (
    <div ref={menuRef} style={{ position:'fixed', top:menuPos.top, left:menuPos.left, width:menuPos.width, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 10px 28px rgba(15,23,42,0.14)', zIndex:1200, minWidth:menuPos.width, maxHeight:menuPos.maxHeight, overflowY:'auto' }}>
      {options.map(opt => (
        <div key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }} style={{ padding:'8px 12px', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8, background: opt.value === value ? '#F9FAFB' : '#fff' }}>
          <span style={{ display:'flex', gap:3, alignItems:'center', width:19, flexShrink:0 }}>
            {opt.colors.map((c,i) => <Dot key={i} color={c}/>)}
          </span>
          {opt.label}
        </div>
      ))}
    </div>
  ) : null;
  return (
    <div ref={ref} style={{ position:'relative', ...style }}>
      <div onClick={() => setOpen(o => !o)} style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:triggerPadding, fontSize:13, background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:6, userSelect:'none', whiteSpace:'nowrap' }}>
        {sel.colors.length > 0 && (
          <span style={{ display:'flex', gap:3, alignItems:'center' }}>
            {sel.colors.map((c,i) => <Dot key={i} color={c}/>)}
          </span>
        )}
        <span style={{ flex:1 }}>{sel.label}</span>
        <ChevronDown size={11} strokeWidth={2.5} style={{ color:'#9CA3AF' }}/>
      </div>
      {menu && typeof ReactDOM !== 'undefined' && ReactDOM.createPortal ? ReactDOM.createPortal(menu, document.body) : menu}
    </div>
  );
};
